import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Wrench, CalendarDays, ShoppingBag, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";

const STATUS_LABELS = { new: "Nouvelle", contacted: "Contacté", received: "Reçu", done: "Terminée", confirmed: "Confirmé", cancelled: "Annulée" };

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="grid gap-4 sm:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}</div>;

  const stats = [
    { label: "Réservations aujourd'hui", value: data.reservations_today, icon: ShoppingBag, testid: "stat-reservations-today" },
    { label: "Devis aujourd'hui", value: data.repairs_today, icon: Wrench, testid: "stat-repairs-today" },
    { label: "RDV aujourd'hui", value: data.appointments_today, icon: CalendarDays, testid: "stat-appointments-today" },
    { label: "Produits dispo", value: data.products_available, icon: Inbox, testid: "stat-products-available" },
  ];

  const lists = [
    { title: "Dernières réservations", items: data.latest_reservations, key: (r) => `${r.product_name} — ${r.first_name}`, ref: "reference", testid: "latest-reservations" },
    { title: "Derniers devis", items: data.latest_repairs, key: (r) => `${r.model_label || r.device_type} — ${r.problem_label}`, ref: "reference", testid: "latest-repairs" },
    { title: "Derniers RDV", items: data.latest_appointments, key: (r) => `${r.name} — ${r.day} ${r.slot}`, ref: "reference", testid: "latest-appointments" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Tableau de bord</h1>
        <span className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold ${data.store_status.open ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid="admin-store-status">
          <span className={`h-2 w-2 rounded-full ${data.store_status.open ? "bg-emerald-500" : "bg-red-500"}`} />
          {data.store_status.label}
        </span>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} data-testid={s.testid} className="card-oxy p-4 sm:p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink text-gold"><Icon className="h-5 w-5" /></span>
              <p className="mt-3 font-display text-3xl font-extrabold text-ink">{s.value}</p>
              <p className="text-xs font-semibold text-slate-500">{s.label}</p>
            </div>
          );
        })}
      </div>

      {data.low_stock.length > 0 && (
        <div className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-5" data-testid="low-stock-alert">
          <h2 className="flex items-center gap-2 font-display font-bold text-amber-800">
            <AlertTriangle className="h-5 w-5" /> Presque épuisés
          </h2>
          <ul className="mt-3 space-y-1.5 text-sm text-amber-900">
            {data.low_stock.map((p) => (
              <li key={p.id} className="flex justify-between gap-3">
                <span>{p.name}</span>
                <span className="font-bold">{p.stock} restant{p.stock > 1 ? "s" : ""}</span>
              </li>
            ))}
          </ul>
          <Link to="/admin/produits" className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-amber-800 hover:underline">
            Gérer le stock <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        {lists.map((l) => (
          <div key={l.title} className="card-oxy p-5" data-testid={l.testid}>
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-slate-900">{l.title}</h2>
              <Link to="/admin/demandes" className="text-xs font-bold text-gold-dark hover:underline">Tout voir</Link>
            </div>
            {l.items.length === 0 ? (
              <p className="mt-4 text-sm text-slate-400">Aucune demande pour le moment.</p>
            ) : (
              <ul className="mt-3 space-y-2.5">
                {l.items.map((r) => (
                  <li key={r.id} className="rounded-xl bg-slate-50 p-3">
                    <p className="text-sm font-semibold text-slate-800 line-clamp-1">{l.key(r)}</p>
                    <p className="mt-0.5 flex items-center justify-between text-xs text-slate-500">
                      <span className="font-mono font-bold text-gold-dark">{r.reference}</span>
                      <span>{STATUS_LABELS[r.status] || r.status}</span>
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
